import { oauthTokenSelector } from 'selectors/app/base'
import { doGraphqlRequest, doAppGraphqlRequest } from 'ducks/requests'
import navigateToLegacy from 'util/navigateToLegacy'
import { selectAccountSubdomain } from 'selectors/app'
import OAuthWindow from 'util/oauth_window'
import { doOpenDrawer } from 'ducks/drawers/actions'
import { DRAWER_TYPE_BILLING_CONFIRM } from 'ducks/drawers/types'
import { selectSearchByQueryId } from 'ducks/searches/selectors'
import { createDoFetchInMemoryByQueryId } from 'ducks/searches/operations/createDoFetchInMemoryByQueryId'
import { invoice } from 'ducks/entities/schema'
import { wait } from 'util/promises'
import { doTryFetchAccountUsageOnboardingForOnboarding } from 'ducks/accountPreferences/operations'
import { onboardingWorkflowData } from 'subapps/onboarding/data'
import {
  basicFields,
  EXTENDED_BILLING_FIELDS,
  fetchBillingData,
  fetchBillingInformation,
  BILLING_INFORMATION_FIELDS,
  CREDIT_CARD_FIELDS,
  INVOICES_DATA_QUERY,
} from '../api'
import {
  UPDATE_CARD,
  START_TRIAL,
  UPDATE_SUBSCRIPTION,
  CANCEL_SUBSCRIPTION,
  FETCH_BILLING_DATA_REQUEST,
  FETCH_BILLING_DATA_SUCCESS,
  FETCH_BILLING_DATA_FAIL,
  FETCH_BILLING_INVOICES,
  FETCH_BILLING_INFORMATION_REQUEST,
  FETCH_BILLING_INFORMATION_SUCCESS,
  FETCH_BILLING_INFORMATION_FAILED,
  UPDATE_BILLING_INFORMATION,
  GET_INVOICE_PDF_LINKS,
  CLOSE_CARD_FAILURE_MODAL,
  SHOPIFY_BILLING_CONFIRMED,
  CANCEL_DOWNGRADE,
} from '../types'
import {
  selectBillingProviderSettings,
  selectBillingPendingSubscription,
} from '../selectors'

export const doFetchBillingData = () => {
  return (dispatch, getState) => {
    const state = getState()
    const token = oauthTokenSelector(state)

    dispatch({ type: FETCH_BILLING_DATA_REQUEST })

    return fetchBillingData(token)
      .then(data => {
        dispatch({ type: FETCH_BILLING_DATA_SUCCESS, payload: data })
        return data
      })
      .catch(err => {
        dispatch({
          type: FETCH_BILLING_DATA_FAIL,
          payload: { error: err },
        })
      })
  }
}

const LOAD_ALL_INVOICES_QUERYID = 'entityType:invoice pageSize:10000'

export const doFetchInvoices = ({ skipLoaded } = {}) => (
  dispatch,
  getState
) => {
  const queryId = LOAD_ALL_INVOICES_QUERYID
  const cursor = 'all'
  const state = getState()
  const { loaded = null, cursors = {} } = selectSearchByQueryId(state, queryId)
  const hasCurrentPage = !!cursors[cursor]
  const isStale = hasCurrentPage && cursors[cursor].isStale

  const hasLoaded = loaded && hasCurrentPage && !isStale

  if (hasLoaded && skipLoaded) {
    // Note we might need to change this in future to return the results
    // from the previous query
    return Promise.resolve({})
  }

  return dispatch(
    doGraphqlRequest(
      FETCH_BILLING_INVOICES,
      INVOICES_DATA_QUERY,
      {},
      {
        transformResponse: res => {
          // NOTE (jscheel): This filters out the leading no-charge invoices
          // from the response, regardless of the sort order they actually come
          // back from the api in. This way we do not display these "trial"
          // invoices to the customer, while still showing other no-charge
          // invoices that may happen later on.
          const sortedInvoices = [...res.invoices].sort(
            (a, b) => a.date - b.date
          )
          const idsForRemoval = new Set()
          // NOTE (jscheel): Array.some bails at the first non-zero total
          sortedInvoices.some(inv => {
            if (inv.total > 0) return true
            idsForRemoval.add(inv.id)
            return false
          })

          return res.invoices.filter(inv => !idsForRemoval.has(inv.id))
        },
        normalizationSchema: [invoice],
        searches: {
          queryId,
          cursor,
          extractPagination: data => {
            return {
              type: 'invoices',
              nodes: data,
              startCursor: 1,
              endCursor: 1,
              hasNextPage: false,
              hasPreviousPage: false,
              totalCount: data.length,
              totalPageCount: 1,
            }
          },
        },
      }
    )
  )
}

export const doFetchBillingInformation = () => {
  return (dispatch, getState) => {
    const state = getState()
    const token = oauthTokenSelector(state)

    dispatch({ type: FETCH_BILLING_INFORMATION_REQUEST })

    return fetchBillingInformation(token)
      .then(data => {
        dispatch({ type: FETCH_BILLING_INFORMATION_SUCCESS, payload: { data } })
      })
      .catch(err => {
        dispatch({
          type: FETCH_BILLING_INFORMATION_FAILED,
          payload: { error: err },
        })
      })
  }
}

const cancelSubscriptionMutation = `
  mutation CancelSubscription($input: [CancellationReason!]!) {
    cancelSubscription(input: $input) {
      ${EXTENDED_BILLING_FIELDS}
    }
  }
`

export const doCancelSubscription = cancellationReasons => {
  return dispatch => {
    dispatch(
      doGraphqlRequest(CANCEL_SUBSCRIPTION, cancelSubscriptionMutation, {
        input: cancellationReasons,
      })
    )
  }
}

const updateSubscriptionMutation = `
  mutation UpdateSubscription(
    $inbox: String
    $kb: String
    $cycle: String!
    $convertTrials: Boolean!
    $legacy: Boolean!
  ) {
    updateSubscription(
      inbox: $inbox,
      kb: $kb,
      cycle: $cycle,
      convertTrials: $convertTrials,
      legacy: $legacy
    ) {
      ${EXTENDED_BILLING_FIELDS}
    }
  }
`

export const doUpdateSubscription = (billing, options = {}) => (
  dispatch,
  getState
) => {
  const { inbox, kb, cycle } = billing
  const { requireExternalApproval } = selectBillingProviderSettings(getState())
  const {
    successMessage,
    failed,
    successMessageDisabled,
    convertTrials = true,
  } =
    options || {}

  return dispatch(
    doGraphqlRequest(
      UPDATE_SUBSCRIPTION,
      updateSubscriptionMutation,
      {
        inbox: inbox === 'legacy' ? null : inbox,
        kb: kb !== 'none' ? kb : null,
        cycle,
        convertTrials,
        legacy: inbox === 'legacy',
      },
      {
        throwOnError: true,
        moduleOptions: {
          toasts: {
            enabled: !requireExternalApproval,
            started: {
              enabled: false,
            },
            success: {
              enabled: !successMessageDisabled,
              content: successMessage || 'Subscription updated',
            },
            failed: {
              enabled: true,
              content: failed || 'Subscription update failed',
              onClickAction: () =>
                dispatch(doUpdateSubscription(billing, options)),
            },
          },
        },
      }
    )
  )
}

const startTrialMutation = `
  mutation StartTrial(
    $product: String!,
  ) {
    startTrial(
      product: $product
    ) {
      ${basicFields}
    }
  }
`

export function doStartTrial(product) {
  return dispatch => {
    const variables = { product }
    return dispatch(
      doGraphqlRequest(START_TRIAL, startTrialMutation, variables, {
        meta: { product },
        transformResponse: data => data.startTrial,
      })
    )
  }
}

export const doRedirectToLegacyBilling = () => (dispatch, getState) => {
  const state = getState()
  const subdomain = selectAccountSubdomain(state)
  const token = oauthTokenSelector(state)

  if (subdomain === undefined) return null

  return navigateToLegacy(token, subdomain, 'settings/billing')
}

const updateBillingInformationMutation = `
  mutation UpdateBillingInformation(
    $input: BillingInformationInput!
  ){
    updateBillingInformation(
      input: $input
    ) {
      ${BILLING_INFORMATION_FIELDS}
    }
  }
`

export const doUpdateBillingInformation = params => dispatch => {
  return dispatch(
    doGraphqlRequest(
      UPDATE_BILLING_INFORMATION,
      updateBillingInformationMutation,
      { input: params },
      {
        moduleOptions: {
          toasts: {
            enabled: true,
            started: {
              enabled: false,
            },
            success: {
              enabled: true,
              content: 'Information changes saved',
            },
            failed: {
              content: 'Information changes failed',
              onClickAction: () => dispatch(doUpdateBillingInformation(params)),
            },
          },
        },
      }
    )
  )
}

const updateCardMutation = `
  mutation ReplaceCreditCard(
    $token: String!
  ){
    replaceCard(
      token: $token
    ) {
      ${CREDIT_CARD_FIELDS}
    }
  }
`

export const doUpdateCard = stripeToken => async dispatch => {
  const response = await dispatch(
    doGraphqlRequest(UPDATE_CARD, updateCardMutation, { token: stripeToken.id })
  )
  dispatch(
    doTryFetchAccountUsageOnboardingForOnboarding(
      onboardingWorkflowData.payment_method?.usageKey
    )
  )
  return response
}

const getInvoicePdfLinksMutation = `
  mutation GetPdfLinksForInvoice(
    $invoiceId: String
  ) {
    getPdfLinksForInvoice(
      invoiceId: $invoiceId
    ) {
      invoicePdf
      receiptPdf
    }
  }
`

export const doGetInvoicePdfLinks = invoiceId => {
  return dispatch => {
    return dispatch(
      doGraphqlRequest(GET_INVOICE_PDF_LINKS, getInvoicePdfLinksMutation, {
        invoiceId,
      })
    )
  }
}

export const doCloseCardFailureError = () => dispatch => {
  return dispatch({
    type: CLOSE_CARD_FAILURE_MODAL,
  })
}

export const doConfirmShopifyActiveSubscription = () => async dispatch => {
  let attempt = 0

  while (attempt <= 30) {
    attempt += 1

    const {
      billing: {
        pendingSubscription: { approvalUrl } = {},
        inbox: { state: inboxState } = {},
      } = {},
    } =
      // eslint-disable-next-line no-await-in-loop
      (await dispatch(doFetchBillingData())) || {}

    // When there is a paid or trial subscription we can continue
    if (['paid', 'trial'].includes(inboxState)) {
      dispatch({ type: SHOPIFY_BILLING_CONFIRMED, payload: {} })
      return true
    }

    // If there is still a pending subscrion, then it means we're still
    // waiting for the webhook to arrive
    if (approvalUrl) {
      // eslint-disable-next-line no-await-in-loop
      await wait(2000)
    } else {
      // If there is no pendingSubscription it means the customer most likely hit cancel
      // on the shopify side
      break
    }
  }
  // Not really my first choice, but this is inline with what the oauth window currently does
  // eslint-disable-next-line no-throw-literal
  throw 'SUBSCRIPTION_NOT_ACTIVE'
}

export const doExternalApprovePendingSubscription = (options = {}) => (
  dispatch,
  getState
) => {
  const {
    oAuthWindowOptions = { width: 600, height: 800 },
    waitForApproval = false,
    redirectFlow = true,
  } = options
  const state = getState()
  const { approvalUrl: url } = selectBillingPendingSubscription(state)

  // If there is no pending subscription, then just assume its already been approved
  if (!url) return Promise.resolve()

  if (redirectFlow) {
    window.location = url
    return Promise.resolve()
  }

  const oAuthFlow = new OAuthWindow({
    title: 'Add Shopify Store',
    url,
    ...oAuthWindowOptions,
  })

  return oAuthFlow
    .start()
    .then(() => {
      if (waitForApproval) {
        return dispatch(doConfirmShopifyActiveSubscription())
      }
      return dispatch(
        doOpenDrawer(
          DRAWER_TYPE_BILLING_CONFIRM,
          DRAWER_TYPE_BILLING_CONFIRM,
          '',
          options
        )
      )
    })
    .catch(err => {
      oAuthFlow.cancel()
      throw err
    })
}

export const doFetchInvoicesInMemory = createDoFetchInMemoryByQueryId({
  fromQueryId: LOAD_ALL_INVOICES_QUERYID,
  entityType: 'invoice',
  doLoadAllFn: doFetchInvoices,
})

const cancelDowngradeMutation = `
mutation CancelDowngrade {
  cancelDowngrade(input: {}) {
    errors {
      message
      path
    }
  }
}
`

export const doCancelDowngrade = (options = {}) => async dispatch => {
  await dispatch(
    doAppGraphqlRequest(
      CANCEL_DOWNGRADE,
      cancelDowngradeMutation,
      {},
      {
        ...options,
        throwOnError: true,
        moduleOptions: {
          toasts: {
            enabled: true,
            started: {
              enabled: false,
            },
            success: {
              enabled: true,
              content: 'Cancelled downgrade successfully',
            },
            failed: {
              enabled: true,
              content: 'Cancel downgrade failed',
              onClickAction: () => dispatch(doCancelDowngrade(options)),
            },
          },
        },
      }
    )
  )
  await dispatch(doFetchBillingData())
  return true
}
